/**
 * Unified, configurable top-view pitch rendering for ECharts.
 * Single source of truth for all pitch drawing (SessionsPitchChart, FreeKickZonePitch, CornerZonePitch, etc.)
 *
 * Features:
 * - Configurable camera/view bounds (full pitch, goal area, etc.)
 * - Optional mowed-grass stripe bands (realistic pitch aesthetic)
 * - Customizable pitch markings (goal, penalty box, six-yard box, center circle, etc.)
 * - Corner flags, penalty spot, goal net rendering
 * - Built-in coordinate transformation (soccer to chart coords)
 * - Reusable pitch color scheme and line styles
 */

import { TargetShapeGeo } from 'types/models/SessionEnrichment';
import { TARGET_ACTIVE_COLOR, TARGET_INACTIVE_COLOR, layerTargets } from 'utilities/targetShapes';
import type { EChartsOption } from 'echarts';

// ─────────────────────────────────────────────────────────
// Pitch Geometry Constants (immutable, shared across all views)
// ─────────────────────────────────────────────────────────

export const GOAL_POST = 3.66;           // Distance between goal posts (meters)
export const GOAL_DEPTH = 2.5;           // Depth of net behind goal line (meters)
export const P_BOX_Z = 20.16;            // Penalty box width from center (meters)
export const P_BOX_X = 16.5;             // Penalty box depth from goal line (meters)
export const SIX_BOX_Z = 9.16;           // Six-yard box half-width (meters)
export const SIX_BOX_X = 5.5;            // Six-yard box depth from goal line (meters)
export const PENALTY_SPOT_X = 11.0;      // Distance from goal line to penalty spot (meters)
export const ARC_RADIUS = 9.15;          // Radius of penalty arc (meters)
export const PITCH_WIDTH = 68.0;         // Full pitch width (meters) — FIFA standard
export const PITCH_LENGTH = 105.0;       // Full pitch length (meters) — FIFA standard
export const HALFWAY_X = 52.5;           // Distance from goal line to halfway (meters)
export const CENTER_RADIUS = 9.15;       // Center circle radius (meters)
export const FULL_PITCH_LENGTH = HALFWAY_X * 2; // Goal line to goal line (meters)

// ─────────────────────────────────────────────────────────
// Color Scheme (consistent across all pitch visualizations)
// ─────────────────────────────────────────────────────────

export const PITCH_GREEN = '#2F7A36';        // Primary grass color
export const PITCH_GREEN_STRIPE = '#287030'; // Alternate stripe color
export const LINE_COLOR = 'rgba(255,255,255,0.92)';    // White pitch markings
export const LINE_WIDTH_MAJOR = 1.6;        // Penalty box, goal area lines
export const LINE_WIDTH_FRAME = 2.2;        // Touchlines, goal line (frame)
export const NET_COLOR = 'rgba(255,255,255,0.25)';     // Goal net grid
export const NET_LINE_WIDTH = 0.8;          // Net line thickness

// ─────────────────────────────────────────────────────────
// Camera Presets (view configurations)
// ─────────────────────────────────────────────────────────

/** Half-pitch goal-area focus: x±36.3m width, z-8.064m to z+28.224m depth (penalty box + buffers) */
export const CAMERA_GOAL_AREA_FOCUS = {
  xMin: -36.3,
  xMax: 36.3,
  yMin: -8.064,  // 40% buffer below goal line
  yMax: 28.224,  // penalty box depth + 40% buffer
} as const;

/** Full-width goal focus: x±36.3m (half pitch), but full vertical depth from goal to halfway */
export const CAMERA_FULL_VERTICAL = {
  xMin: -36.3,
  xMax: 36.3,
  yMin: -5.0,
  yMax: 52.5,  // To halfway line
} as const;

/** Full-pitch view: x±52.5m, y-5 to y+52.5 */
export const CAMERA_FULL_PITCH = {
  xMin: -52.5,
  xMax: 52.5,
  yMin: -5.0,
  yMax: 52.5,
} as const;

/**
 * Depth window (yAxis, metres from the goal line) for a goal-area top view in a container of the given height / width
 * ratio. The lateral window stays fixed (±36.3m), so a taller container shows more of the pitch instead of stretching
 * it: extra depth goes towards midfield first (up to the camera's own yMax of the full half-pitch), then behind the goal.
 * Never narrower than the default focus window.
 */
export function goalAreaDepthWindow(aspect: number | null): { yMin: number; yMax: number } {
  const base = CAMERA_GOAL_AREA_FOCUS;
  const width = base.xMax - base.xMin;
  const baseRange = base.yMax - base.yMin;
  if (aspect == null || !Number.isFinite(aspect)) return { yMin: base.yMin, yMax: base.yMax };
  const extra = Math.max(0, width * aspect - baseRange);
  const yMax = Math.min(base.yMax + extra, CAMERA_FULL_VERTICAL.yMax);
  const leftover = extra - (yMax - base.yMax);
  return { yMin: base.yMin - leftover, yMax };
}

/**
 * Landscape view window for a container of the given width / height ratio: the whole pitch turned 90° so the goal line
 * is the left edge, depth runs right along the full 105m length and the full width (±36.3m with margin) fits vertically.
 * The window keeps the container's shape: a wide box shows the pitch centred with margin at both ends, a narrower one
 * widens the sideways range instead of cropping the length.
 */
export function landscapeWindow(widthOverHeight: number | null): { zHalf: number; yMin: number; yMax: number } {
  const ratio = widthOverHeight != null && Number.isFinite(widthOverHeight) && widthOverHeight > 0 ? widthOverHeight : 1.6;
  const lengthWithMargin = FULL_PITCH_LENGTH + 6; // 3m of grass beyond each goal line
  const zHalf = Math.max(CAMERA_GOAL_AREA_FOCUS.xMax, lengthWithMargin / (2 * ratio));
  const depth = 2 * zHalf * ratio;
  const yMin = HALFWAY_X - depth / 2;
  return { zHalf, yMin, yMax: yMin + depth };
}

/**
 * Turns a top-view option 90° (goal on the left, depth running right, lateral position vertical) by swapping the two
 * axes and the first two coordinates of every scatter / effectScatter / line point. Series built for the portrait view
 * can be passed through unchanged; callers swap any pixel↔data conversion of their own the same way.
 */
export function transposeTopViewOption<T extends { xAxis?: unknown; yAxis?: unknown; series?: unknown }>(option: T): T {
  const swapPoint = (item: any): any => {
    if (Array.isArray(item)) return [item[1], item[0], ...item.slice(2)];
    if (item && Array.isArray(item.value)) return { ...item, value: swapPoint(item.value) };
    return item;
  };
  const series = (Array.isArray(option.series) ? option.series : []).map((s: any) => (
    ['scatter', 'effectScatter', 'line'].includes(s?.type) && Array.isArray(s.data) ? { ...s, data: s.data.map(swapPoint) } : s
  ));
  return {
    ...option,
    xAxis: { ...(option.yAxis as object), inverse: false },
    yAxis: { ...(option.xAxis as object), inverse: false },
    series,
  };
}

// ─────────────────────────────────────────────────────────
// Drawing Configuration
// ─────────────────────────────────────────────────────────

export interface TopViewPitchConfig {
  /** Camera view bounds */
  camera: {
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
  };

  /** Show mowed grass stripe bands for realistic pitch aesthetic (default: true) */
  showStripes?: boolean;

  /** Show penalty box and goal area markings (default: true) */
  showPenaltyBox?: boolean;

  /** Show six-yard box markings (default: true) */
  showSixYardBox?: boolean;

  /** Show center circle and halfway line (default: true) */
  showCenterCircle?: boolean;

  /** Show goal net grid (default: true) */
  showGoalNet?: boolean;

  /** Show goal posts and goal line (default: true) */
  showGoal?: boolean;

  /** Show penalty spot (default: true) */
  showPenaltySpot?: boolean;

  /** Draw the far half too (mirrored penalty area, goal, full centre circle): the whole 105m pitch (default: false) */
  showFarHalf?: boolean;

  /** Show corner flags (default: false — specific to corner zone visualization) */
  showCornerFlags?: boolean;

  /** Show penalty arc (default: true) */
  showPenaltyArc?: boolean;

  /** Show touchlines (side and goal lines as frame) (default: true) */
  showTouchlines?: boolean;

  /** Line color override (default: LINE_COLOR) */
  lineColor?: string;

  /** Grass color override (default: PITCH_GREEN) */
  grassColor?: string;

  /** z-index for pitch base series (default: 0) */
  zIndex?: number;
}

// ─────────────────────────────────────────────────────────
// Pitch Drawing Primitives
// ─────────────────────────────────────────────────────────

function pitchLineSeries(
  id: string,
  data: [number, number][],
  color: string = LINE_COLOR,
  width: number = LINE_WIDTH_MAJOR,
  zIndex: number = 2
) {
  return {
    id,
    type: 'line' as const,
    data,
    symbol: 'none' as const,
    lineStyle: { color, width },
    silent: true,
    animation: false,
    z: zIndex,
  };
}

function buildGoalNetLines(): Array<[[number, number], [number, number]]> {
  const segments: Array<[[number, number], [number, number]]> = [];
  const netSpacing = 0.4;
  
  // Vertical mesh lines (perpendicular to goal line)
  for (let z = -GOAL_POST; z <= GOAL_POST; z += netSpacing) {
    segments.push([[z, 0], [z, -GOAL_DEPTH]]);
  }
  
  // Horizontal mesh lines (parallel to goal line, at different depths)
  const depthSteps = Math.ceil(GOAL_DEPTH / netSpacing);
  for (let i = 1; i < depthSteps; i++) {
    const depth = -((i / depthSteps) * GOAL_DEPTH);
    segments.push([[-GOAL_POST, depth], [GOAL_POST, depth]]);
  }
  
  return segments;
}

/** Points of a full circle in chart coordinates `[lateral, depth]`, closed (first point repeated at the end). */
export function circlePoints(centreLateral: number, centreDepth: number, radius: number, steps: number): [number, number][] {
  return Array.from({ length: steps + 1 }, (_, i) => {
    const a = (i / steps) * Math.PI * 2;
    return [centreLateral + radius * Math.sin(a), centreDepth + radius * Math.cos(a)] as [number, number];
  });
}

function buildPenaltyArc(): [number, number][] {
  const arcAngleCutoff = Math.acos((P_BOX_X - PENALTY_SPOT_X) / ARC_RADIUS);
  const pts: [number, number][] = [];
  for (let i = 0; i <= 60; i++) {
    const a = -arcAngleCutoff + (2 * arcAngleCutoff) * (i / 60);
    pts.push([ARC_RADIUS * Math.sin(a), PENALTY_SPOT_X + ARC_RADIUS * Math.cos(a)]);
  }
  return pts;
}

function buildCenterCircle(yMin: number, yMax: number): [number, number][] {
  const pts: [number, number][] = [];
  // Draw only visible arc of center circle based on yMin/yMax
  const arcAngleCutoff = Math.acos((HALFWAY_X - CENTER_RADIUS) / CENTER_RADIUS);
  for (let i = 0; i <= 60; i++) {
    const a = -arcAngleCutoff + (2 * arcAngleCutoff) * (i / 60);
    const y = HALFWAY_X + CENTER_RADIUS * Math.cos(a);
    if (y >= yMin && y <= yMax) {
      pts.push([CENTER_RADIUS * Math.sin(a), y]);
    }
  }
  return pts;
}

function buildStripeBands(camera: TopViewPitchConfig['camera'], grassColor: string): any[] {
  const bands: any[] = [];
  const fieldLeft = -33;
  const fieldRight = 33;
  const stripeDepth = 8;
  const fieldTop = Math.max(camera.yMin, -10);
  const fieldBottom = Math.min(camera.yMax, HALFWAY_X + 10);

  for (let i = 0; i < Math.ceil((fieldBottom - fieldTop) / stripeDepth); i++) {
    if (i % 2 === 0) continue;
    const from = fieldTop + i * stripeDepth;
    const to = Math.min(fieldBottom, from + stripeDepth);
    bands.push({
      type: 'markArea' as const,
      itemStyle: { color: grassColor, opacity: 0.3 },
      data: [
        [
          { coord: [fieldLeft, from] },
          { coord: [fieldRight, to] },
        ],
      ],
    });
  }
  return bands;
}

// ─────────────────────────────────────────────────────────
// Main Builder: Unified Top View Pitch Option
// ─────────────────────────────────────────────────────────

/**
 * Build a complete ECharts option for top-view pitch rendering.
 * Configurable for different camera angles, field sizes, and drawing elements.
 *
 * @param config - TopViewPitchConfig with camera bounds and feature flags
 * @returns ECharts option ready to render
 */
export function buildTopViewPitchOption(config: TopViewPitchConfig): EChartsOption {
  const {
    camera,
    showStripes = true,
    showPenaltyBox = true,
    showSixYardBox = true,
    showCenterCircle = true,
    showGoalNet = true,
    showGoal = true,
    showPenaltySpot = true,
    showCornerFlags = false,
    showFarHalf = false,
    showPenaltyArc = true,
    showTouchlines = true,
    lineColor = LINE_COLOR,
    grassColor = PITCH_GREEN,
    zIndex = 0,
  } = config;

  const series: EChartsOption['series'] = [];
  const markArea: any[] = [];

  // Background grass color
  const grid = {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: grassColor,
  };

  // Stripe bands (mowed grass effect)
  if (showStripes) {
    markArea.push(...buildStripeBands(camera, grassColor));
  }

  // Touchlines (frame: side lines and goal line)
  if (showTouchlines) {
    // Side lines extend from goal line (x=0) to middle of pitch (camera.yMax), not behind goal
    // Goal is at y=0, field extends to camera.yMax (positive X towards midfield)
    series.push(
      pitchLineSeries(
        'side-line-left',
        [[-33, 0], [-33, camera.yMax]],
        lineColor,
        LINE_WIDTH_FRAME,
        zIndex + 2
      ),
      pitchLineSeries(
        'side-line-right',
        [[33, 0], [33, camera.yMax]],
        lineColor,
        LINE_WIDTH_FRAME,
        zIndex + 2
      ),
      pitchLineSeries(
        'goal-line',
        [[-33, 0], [33, 0]],
        lineColor,
        LINE_WIDTH_FRAME,
        zIndex + 2
      )
    );
  }

  // Penalty box
  if (showPenaltyBox) {
    series.push(
      pitchLineSeries(
        'penalty-box-top',
        [[-P_BOX_Z, P_BOX_X], [P_BOX_Z, P_BOX_X]],
        lineColor,
        LINE_WIDTH_MAJOR,
        zIndex + 2
      ),
      pitchLineSeries(
        'penalty-box-left',
        [[-P_BOX_Z, 0], [-P_BOX_Z, P_BOX_X]],
        lineColor,
        LINE_WIDTH_MAJOR,
        zIndex + 2
      ),
      pitchLineSeries(
        'penalty-box-right',
        [[P_BOX_Z, 0], [P_BOX_Z, P_BOX_X]],
        lineColor,
        LINE_WIDTH_MAJOR,
        zIndex + 2
      )
    );
  }

  // Six-yard box
  if (showSixYardBox) {
    series.push(
      pitchLineSeries(
        'six-yard-box-top',
        [[-SIX_BOX_Z, SIX_BOX_X], [SIX_BOX_Z, SIX_BOX_X]],
        lineColor,
        LINE_WIDTH_MAJOR,
        zIndex + 2
      ),
      pitchLineSeries(
        'six-yard-box-left',
        [[-SIX_BOX_Z, 0], [-SIX_BOX_Z, SIX_BOX_X]],
        lineColor,
        LINE_WIDTH_MAJOR,
        zIndex + 2
      ),
      pitchLineSeries(
        'six-yard-box-right',
        [[SIX_BOX_Z, 0], [SIX_BOX_Z, SIX_BOX_X]],
        lineColor,
        LINE_WIDTH_MAJOR,
        zIndex + 2
      )
    );
  }

  // Goal net
  if (showGoalNet) {
    const netLines = buildGoalNetLines();
    // Create individual series for each mesh line segment
    for (const segment of netLines) {
      series.push(
        pitchLineSeries(`goal-net-segment-${series.length}`, segment, NET_COLOR, NET_LINE_WIDTH, zIndex + 1)
      );
    }
    // Goal net frame (border posts and back)
    series.push(
      pitchLineSeries(
        'goal-net-frame-left',
        [[-GOAL_POST, 0], [-GOAL_POST, -GOAL_DEPTH]],
        lineColor,
        LINE_WIDTH_MAJOR,
        zIndex + 2
      ),
      pitchLineSeries(
        'goal-net-frame-right',
        [[GOAL_POST, 0], [GOAL_POST, -GOAL_DEPTH]],
        lineColor,
        LINE_WIDTH_MAJOR,
        zIndex + 2
      ),
      pitchLineSeries(
        'goal-net-frame-back',
        [[-GOAL_POST, -GOAL_DEPTH], [GOAL_POST, -GOAL_DEPTH]],
        lineColor,
        LINE_WIDTH_MAJOR,
        zIndex + 2
      )
    );
  }

  // Goal line (if not already drawn as part of touchlines)
  if (showGoal && !showTouchlines) {
    series.push(
      pitchLineSeries(
        'goal-line-explicit',
        [[-33, 0], [33, 0]],
        lineColor,
        LINE_WIDTH_FRAME,
        zIndex + 2
      )
    );
  }

  // Penalty spot
  if (showPenaltySpot) {
    series.push({
      id: 'penalty-spot',
      type: 'scatter' as const,
      data: [[0, PENALTY_SPOT_X]],
      symbolSize: 3,
      itemStyle: { color: lineColor },
      silent: true,
      animation: false,
      z: zIndex + 2,
    } as any);
  }

  // Penalty arc
  if (showPenaltyArc) {
    const arcPts = buildPenaltyArc();
    series.push(
      pitchLineSeries('penalty-arc', arcPts, lineColor, LINE_WIDTH_MAJOR, zIndex + 2)
    );
  }

  // Center circle (if visible in view)
  if (showCenterCircle && camera.yMax > 40) {
    const centerPts = buildCenterCircle(camera.yMin, camera.yMax);
    if (centerPts.length > 0) {
      series.push(
        pitchLineSeries(
          'center-circle',
          centerPts,
          lineColor,
          LINE_WIDTH_MAJOR,
          zIndex + 2
        )
      );
      // Halfway line
      series.push(
        pitchLineSeries(
          'halfway-line',
          [[-33, HALFWAY_X], [33, HALFWAY_X]],
          lineColor,
          LINE_WIDTH_MAJOR,
          zIndex + 2
        )
      );
    }
  }

  // Corner flags (optional, specific to corner zone visualization)
  if (showCornerFlags) {
    series.push(
      {
        id: 'corner-flag-left',
        type: 'custom' as const,
        z: zIndex + 4,
        silent: false,
        animation: false,
        data: [{ value: 0 }],
        flagPosition: [-33, 0] as [number, number],
        itemStyle: { color: '#ff6b6b', selectedColor: '#4f46e5' },
      } as any,
      {
        id: 'corner-flag-right',
        type: 'custom' as const,
        z: zIndex + 4,
        silent: false,
        animation: false,
        data: [{ value: 0 }],
        flagPosition: [33, 0] as [number, number],
        itemStyle: { color: '#ff6b6b', selectedColor: '#4f46e5' },
      } as any
    );
  }

  // Whole pitch: mirror the near-half markings onto the far goal line and complete the centre circle.
  if (showFarHalf) {
    const mirror = (pts: [number, number][]): [number, number][] => pts.map(([z, x]) => [z, FULL_PITCH_LENGTH - x]);
    const nearMarkings = /^(penalty-box|six-yard-box|goal-net|goal-line|penalty-arc)/;
    const farSeries: any[] = [];
    (series as any[]).forEach((item) => {
      if (item.type === 'line' && nearMarkings.test(item.id ?? '')) farSeries.push({ ...item, id: `${item.id}-far`, data: mirror(item.data) });
      if (item.id === 'penalty-spot') farSeries.push({ ...item, id: 'penalty-spot-far', data: mirror(item.data) });
    });
    const fullCircle = circlePoints(0, HALFWAY_X, CENTER_RADIUS, 90);
    series.push(
      ...farSeries,
      pitchLineSeries('center-circle-full', fullCircle, lineColor, LINE_WIDTH_MAJOR, zIndex + 2),
      pitchLineSeries('halfway-line-full', [[-33, HALFWAY_X], [33, HALFWAY_X]], lineColor, LINE_WIDTH_MAJOR, zIndex + 2),
    );
  }

  return {
    grid,
    xAxis: {
      type: 'value' as const,
      boundaryGap: false,
      min: camera.xMin,
      max: camera.xMax,
      splitLine: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
    } as any,
    yAxis: {
      type: 'value' as const,
      boundaryGap: false,
      min: camera.yMin,
      max: camera.yMax,
      inverse: true,
      splitLine: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
    } as any,
    series,
    ...(markArea.length > 0 && { markArea }),
  };
}

/**
 * Session targets (goalkeeper circles / corner points / rectangles) as one `custom` series drawn flat on the pitch. The
 * aimed-at target (`isActive`) is yellow, solid and more opaque; the others are gray and faint. `toChartZ` maps a
 * soccer-space lateral value to the chart's lateral axis; `landscape` says the axes are turned (depth first).
 */
export function buildTargetSeries(
  targets: ReadonlyArray<TargetShapeGeo> | undefined,
  toChartZ: (soccerZ: number) => number,
  landscape: boolean,
): any[] {
  const layered = layerTargets(targets);
  if (layered.length === 0) return [];
  return [{
    id: 'session-targets',
    type: 'custom',
    silent: true,
    animation: false,
    z: 3,
    data: layered.map((_, index) => [index]),
    renderItem: (params: any, api: any) => {
      const target = layered[params.dataIndex];
      if (!target) return null;
      const active = Boolean(target.isActive);
      const color = active ? TARGET_ACTIVE_COLOR : TARGET_INACTIVE_COLOR;
      const [depth, , lateralSoccer] = target.center;
      const lateral = toChartZ(lateralSoccer);
      const px = (dLat: number, dDepth: number): [number, number] => api.coord(landscape ? [depth + dDepth, lateral + dLat] : [lateral + dLat, depth + dDepth]);
      const circle = (radius: number): [number, number][] => circlePoints(0, 0, radius, 48).map(([dLat, dDepth]) => px(dLat, dDepth));
      const children: any[] = [];
      if (target.shape === 'circle') {
        const inner = Math.max(0, target.innerRadius ?? 0);
        const outer = Math.max(inner + 0.1, target.outerRadius ?? inner + 0.3);
        if (inner > 0) children.push({ type: 'polygon', shape: { points: circle(inner) }, style: { fill: color, opacity: active ? 0.6 : 0.2 }, silent: true });
        children.push({ type: 'polyline', shape: { points: circle(outer) }, style: { stroke: color, fill: 'none', lineWidth: active ? 2 : 1.2, lineDash: [6, 4], opacity: active ? 1 : 0.55 }, silent: true });
      } else if (target.shape === 'rectangle') {
        const halfDepth = Math.max(0.1, target.xLength ?? 1) / 2;
        const halfLat = Math.max(0.1, target.zLength ?? 1) / 2;
        const corners = [px(-halfLat, -halfDepth), px(halfLat, -halfDepth), px(halfLat, halfDepth), px(-halfLat, halfDepth)];
        children.push({ type: 'polygon', shape: { points: corners }, style: { fill: color, stroke: color, lineWidth: 1, opacity: active ? 0.55 : 0.2 }, silent: true });
      } else {
        children.push({ type: 'polyline', shape: { points: circle(0.4) }, style: { stroke: color, fill: 'none', lineWidth: active ? 3 : 2, opacity: active ? 1 : 0.55 }, silent: true });
      }
      return { type: 'group', children };
    },
  }];
}
